/**
 * Frame outbox for WAN outages (M05 acceptance, SF-18). The office gateway
 * and the browser board both keep frames they could not deliver and replay
 * them in order when the line is back, with their original time and their
 * original frame ids (the API ignores duplicates).
 *
 * Capacity is 5,000 frames (Pulse Status gateway queue). When full, the
 * oldest low-priority frame is dropped first; urgent and emergency frames
 * are dropped only when nothing else is left, and the drop is counted so the
 * board can say so. Retry delays follow the Pulse Status gateway: 500 ms
 * doubling to 8 s.
 */
import {
  GATEWAY_QUEUE_CAPACITY,
  type LightboardFrame,
  type Priority,
} from '@velora/contracts/lightboard';

export interface QueuedFrame {
  /** Office the frame belongs to (the gateway serves one office). */
  locationId: string;
  frame: LightboardFrame;
  priority: Priority;
  /** Local time the frame was queued, for display only. */
  queuedAt: number;
}

export interface OutboxSnapshot {
  items: QueuedFrame[];
  dropped: number;
}

const DROP_ORDER: readonly Priority[] = ['low', 'normal', 'high', 'urgent', 'emergency'];

export class FrameOutbox {
  private items: QueuedFrame[] = [];
  private droppedCount = 0;

  constructor(
    readonly capacity: number = GATEWAY_QUEUE_CAPACITY,
    snapshot?: OutboxSnapshot,
  ) {
    if (snapshot) {
      this.items = [...snapshot.items];
      this.droppedCount = snapshot.dropped;
      while (this.items.length > capacity) this.dropOne();
    }
  }

  get size(): number {
    return this.items.length;
  }

  get dropped(): number {
    return this.droppedCount;
  }

  /** Adds a frame (idempotent by frame id). Returns false when it was a duplicate. */
  push(item: QueuedFrame): boolean {
    if (this.items.some((i) => i.frame.frameId === item.frame.frameId)) return false;
    this.items.push(item);
    while (this.items.length > this.capacity) this.dropOne();
    return true;
  }

  /** Frames for one office in queue order, at most `max`. */
  peek(locationId: string, max: number): QueuedFrame[] {
    return this.items.filter((i) => i.locationId === locationId).slice(0, max);
  }

  /** Offices with queued frames, in order of their oldest frame. */
  locations(): string[] {
    return [...new Set(this.items.map((i) => i.locationId))];
  }

  /** Removes delivered (or permanently refused) frames. */
  ack(frameIds: Iterable<string>): void {
    const done = new Set(frameIds);
    this.items = this.items.filter((i) => !done.has(i.frame.frameId));
  }

  snapshot(): OutboxSnapshot {
    return { items: [...this.items], dropped: this.droppedCount };
  }

  private dropOne(): void {
    for (const p of DROP_ORDER) {
      const idx = this.items.findIndex((i) => i.priority === p);
      if (idx >= 0) {
        this.items.splice(idx, 1);
        this.droppedCount += 1;
        return;
      }
    }
  }
}

/** Retry delay for attempt n (0-based): 500 ms doubling, capped at 8 s, with up to 20% jitter. */
export function retryDelayMs(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(8000, 500 * 2 ** Math.max(0, attempt));
  return Math.round(base * (1 - 0.2 * random()));
}

/** Network failures queue the frame; an HTTP answer (even an error) means the server decided. */
export function shouldQueue(error: unknown): boolean {
  if (error instanceof TypeError) return true; // fetch: network down, DNS, CORS abort
  const status = (error as { status?: unknown } | null)?.status;
  return status === 502 || status === 503 || status === 504;
}
